import React, { useEffect } from "react";

const toAbsoluteUrl = (value) => {
  if (!value) return undefined;
  try {
    return new URL(value).toString();
  } catch {
    if (typeof window === "undefined") return undefined;
    try {
      return new URL(value, window.location.origin).toString();
    } catch {
      return undefined;
    }
  }
};

const setManagedMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("data-seo-managed", "true");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    if (value) tag.setAttribute(name, value);
  });
};

const SEO = ({
  title,
  description,
  canonicalUrl,
  language,
  robots,
  openGraph = {},
  twitter = {},
  alternates = [],
  schema,
}) => {
  useEffect(() => {
    const managedNodes = [];

    document.head.querySelectorAll('[data-seo-managed="true"]').forEach((node) => node.remove());
    document.head.querySelectorAll('link[rel="canonical"]').forEach((node) => node.remove());

    const currentLocation = window.location.href.split("?")[0];
    const resolvedCanonicalUrl = toAbsoluteUrl(canonicalUrl) || toAbsoluteUrl(currentLocation);
    const fallbackImage = resolvedCanonicalUrl ? `${new URL(resolvedCanonicalUrl).origin}/favicon.png` : undefined;
    const resolvedImage = openGraph?.image || twitter?.image || fallbackImage;

    const resolvedOpenGraph = {
      title,
      description,
      url: resolvedCanonicalUrl,
      type: "website",
      image: resolvedImage,
      ...openGraph,
    };

    const resolvedTwitter = {
      card: "summary",
      title,
      description,
      image: resolvedImage,
      ...twitter,
    };

    if (title) document.title = title;

    if (description) {
      setManagedMetaTag('meta[name="description"]', { name: "description", content: description });
    }

    if (language) {
      setManagedMetaTag('meta[http-equiv="content-language"]', { "http-equiv": "content-language", content: language });
    }

    if (robots) {
      setManagedMetaTag('meta[name="robots"]', { name: "robots", content: robots });
    }

    const metaPairs = [
      ["og:title", resolvedOpenGraph?.title],
      ["og:description", resolvedOpenGraph?.description],
      ["og:url", resolvedOpenGraph?.url],
      ["og:type", resolvedOpenGraph?.type],
      ["og:image", resolvedOpenGraph?.image],
      ["og:site_name", resolvedOpenGraph?.siteName],
      ["og:locale", resolvedOpenGraph?.locale],
    ];

    metaPairs.forEach(([property, content]) => {
      if (!content) return;
      setManagedMetaTag(`meta[property="${property}"]`, { property, content });
    });

    const twitterPairs = [
      ["twitter:card", resolvedTwitter?.card],
      ["twitter:site", resolvedTwitter?.site],
      ["twitter:creator", resolvedTwitter?.creator],
      ["twitter:title", resolvedTwitter?.title],
      ["twitter:description", resolvedTwitter?.description],
      ["twitter:image", resolvedTwitter?.image],
    ];

    twitterPairs.forEach(([name, content]) => {
      if (!content) return;
      setManagedMetaTag(`meta[name="${name}"]`, { name, content });
    });

    if (resolvedCanonicalUrl) {
      const canonical = document.createElement("link");
      canonical.rel = "canonical";
      canonical.href = resolvedCanonicalUrl;
      canonical.setAttribute("data-seo-managed", "true");
      document.head.appendChild(canonical);
      managedNodes.push(canonical);
    }

    document.head.querySelectorAll('link[rel="alternate"]').forEach((node) => {
      if (node.getAttribute('data-seo-managed') === 'true' || node.hrefLang) node.remove();
    });

    alternates
      .map((alt) => ({
        ...alt,
        href: toAbsoluteUrl(alt?.href),
      }))
      .filter((alt, index, list) => alt?.hrefLang && alt?.href && list.findIndex((item) => item?.hrefLang === alt.hrefLang && item?.href === alt.href) === index)
      .forEach((alternate) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hrefLang = alternate.hrefLang;
        link.href = alternate.href;
        link.setAttribute("data-seo-managed", "true");
        document.head.appendChild(link);
        managedNodes.push(link);
      });

    if (schema) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = typeof schema === "string" ? schema : JSON.stringify(schema, null, 2);
      script.setAttribute("data-seo-managed", "true");
      document.head.appendChild(script);
      managedNodes.push(script);
    }

    return () => {
      managedNodes.forEach((node) => node.remove());
    };
  }, [alternates, canonicalUrl, description, language, openGraph, robots, schema, title, twitter]);

  return null;
};

export default SEO;
